// Encrypted files. Each file gets its own AES-256-GCM key, which travels inside the end-to-end
// encrypted message. The server and the object store only ever hold ciphertext.

import type { Api } from "./api";
import {
  decryptStream,
  isChunked,
  MediaVersionError,
  mediaSupport,
  type RangeFetcher,
  type StreamOptions,
  streamToBlob,
  urlRangeFetcher,
} from "./chunked";
import type { MediaRef } from "./content";

/** The server refuses uploads above 2 GB. */
export const MAX_MEDIA_BYTES = 2 * 1024 * 1024 * 1024;
/** Above this the app asks before uploading. */
export const WARN_MEDIA_BYTES = 100 * 1024 * 1024;
/** How much decrypted media the in-memory cache keeps. */
export const MEMORY_CACHE_BYTES = 100 * 1024 * 1024;

const GCM_TAG_BYTES = 16;
const CACHE_NAME = "rhema-media-v1";

export class MediaTooLargeError extends Error {
  constructor(readonly size: number) {
    super("Files can be up to 2 GB.");
  }
}

export class MediaIntegrityError extends Error {
  constructor() {
    super("This file did not match what the sender sent, so it was not opened.");
  }
}

/** What is known about a file before it is encrypted. */
export type FileMeta = Partial<Omit<MediaRef, "key" | "file_key" | "iv" | "sha256" | "size">>;

export function toBase64(bytes: Uint8Array): string {
  let out = "";
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) out += String.fromCharCode(...bytes.subarray(i, i + step));
  return btoa(out);
}

export function fromBase64(text: string): Uint8Array<ArrayBuffer> {
  const raw = atob(text);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function asBytes(data: Uint8Array | ArrayBuffer): Uint8Array<ArrayBuffer> {
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  // WebCrypto wants a view over a plain ArrayBuffer. Copy only when it is shared memory.
  if (data.buffer instanceof ArrayBuffer) return data as Uint8Array<ArrayBuffer>;
  return new Uint8Array(data);
}

/** Base64 SHA-256 of the bytes. */
export async function sha256(data: Uint8Array | ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", asBytes(data));
  return toBase64(new Uint8Array(digest));
}

/**
 * Encrypts a file with a fresh 256-bit key and 96-bit IV. The returned ref has an empty storage
 * key, which the upload fills in.
 */
export async function encryptFile(
  data: Uint8Array | ArrayBuffer,
  meta: FileMeta = {},
): Promise<{ ciphertext: Uint8Array<ArrayBuffer>; ref: MediaRef }> {
  const bytes = asBytes(data);
  if (bytes.byteLength > MAX_MEDIA_BYTES - GCM_TAG_BYTES) throw new MediaTooLargeError(bytes.byteLength);
  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await crypto.subtle.importKey("raw", rawKey, "AES-GCM", false, ["encrypt"]);
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, bytes));
  const ref: MediaRef = {
    ...meta,
    key: "",
    file_key: toBase64(rawKey),
    iv: toBase64(iv),
    sha256: await sha256(ciphertext),
    mime: meta.mime || "application/octet-stream",
    size: bytes.byteLength,
  };
  return { ciphertext, ref };
}

/** Checks the hash, then decrypts. A file that was changed in storage is refused. */
export async function decryptFile(
  ciphertext: Uint8Array | ArrayBuffer,
  ref: Pick<MediaRef, "file_key" | "iv" | "sha256">,
): Promise<Uint8Array<ArrayBuffer>> {
  const bytes = asBytes(ciphertext);
  if ((await sha256(bytes)) !== ref.sha256) throw new MediaIntegrityError();
  const key = await crypto.subtle.importKey("raw", fromBase64(ref.file_key), "AES-GCM", false, ["decrypt"]);
  try {
    const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromBase64(ref.iv) }, key, bytes);
    return new Uint8Array(plain);
  } catch {
    throw new MediaIntegrityError();
  }
}

/** Least recently used cache of decrypted files, bounded by total size. */
export class BlobCache {
  private items = new Map<string, Blob>();
  private bytes = 0;

  constructor(readonly limit = MEMORY_CACHE_BYTES) {}

  get(key: string): Blob | undefined {
    const blob = this.items.get(key);
    if (!blob) return undefined;
    this.items.delete(key);
    this.items.set(key, blob);
    return blob;
  }

  set(key: string, blob: Blob): void {
    this.delete(key);
    if (blob.size > this.limit) return;
    this.items.set(key, blob);
    this.bytes += blob.size;
    for (const [oldest, old] of this.items) {
      if (this.bytes <= this.limit) break;
      this.items.delete(oldest);
      this.bytes -= old.size;
    }
  }

  delete(key: string): void {
    const blob = this.items.get(key);
    if (!blob) return;
    this.items.delete(key);
    this.bytes -= blob.size;
  }

  get size(): number {
    return this.bytes;
  }

  has(key: string): boolean {
    return this.items.has(key);
  }
}

export type UploadProgress = (fraction: number) => void;
export type Uploader = (
  url: string,
  body: Uint8Array<ArrayBuffer>,
  onProgress?: UploadProgress,
  /** Stops the upload part way, when the person cancels it. */
  signal?: AbortSignal,
) => Promise<void>;

/** Thrown when the person cancels an upload. */
export class UploadCancelledError extends Error {
  constructor() {
    super("The upload was cancelled.");
    this.name = "UploadCancelledError";
  }
}

/** PUTs the ciphertext. XHR in the browser so progress can be shown, fetch everywhere else. */
export const defaultUploader: Uploader = (url, body, onProgress, signal) => {
  if (signal?.aborted) return Promise.reject(new UploadCancelledError());
  if (typeof XMLHttpRequest === "undefined") {
    return fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/octet-stream" },
      body,
      signal,
    }).then((res) => {
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      onProgress?.(1);
    });
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Upload failed (${xhr.status})`));
        return;
      }
      onProgress?.(1);
      resolve();
    };
    xhr.onerror = () => reject(new Error("The upload was interrupted."));
    xhr.onabort = () => reject(new UploadCancelledError());
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(body);
  });
};

export type MediaOptions = {
  upload?: Uploader;
  /** Used to download ciphertext from the presigned URL. */
  fetch?: typeof fetch;
  /** Cache API storage for ciphertext. Pass null to turn it off. */
  caches?: CacheStorage | null;
  memoryLimit?: number;
};

function cacheRequest(key: string): string {
  return `https://rhema.invalid/media/${encodeURIComponent(key)}`;
}

/** Uploads and downloads encrypted files for one signed-in client. */
export class MediaClient {
  readonly memory: BlobCache;
  private readonly upload: Uploader;
  private readonly doFetch: typeof fetch;
  private readonly cacheStorage: CacheStorage | null;
  private readonly inflight = new Map<string, Promise<Blob>>();

  constructor(
    private readonly api: Api,
    opts: MediaOptions = {},
  ) {
    this.upload = opts.upload ?? defaultUploader;
    this.doFetch = opts.fetch ?? ((...args) => fetch(...args));
    this.cacheStorage =
      opts.caches !== undefined ? opts.caches : typeof caches === "undefined" ? null : caches;
    this.memory = new BlobCache(opts.memoryLimit);
  }

  /** Encrypts and uploads a file to a chat. Returns the ref to put in the message. */
  async send(
    chatId: string,
    data: Uint8Array | ArrayBuffer,
    meta: FileMeta,
    onProgress?: UploadProgress,
    /** "import" for files from an imported chat history, which have their own rate limit. */
    purpose?: "import",
    /** Cancels the upload. */
    signal?: AbortSignal,
  ): Promise<MediaRef> {
    const stopIfCancelled = () => {
      if (signal?.aborted) throw new UploadCancelledError();
    };
    const { ciphertext, ref } = await encryptFile(data, meta);
    stopIfCancelled();
    const slot = purpose
      ? await this.api.createUpload(chatId, ciphertext.byteLength, purpose)
      : await this.api.createUpload(chatId, ciphertext.byteLength);
    stopIfCancelled();
    if (signal) await this.upload(slot.url, ciphertext, onProgress, signal);
    else await this.upload(slot.url, ciphertext, onProgress);
    const done = { ...ref, key: slot.key };
    // The sender sees their own file at once, without downloading it back.
    this.memory.set(done.key, new Blob([asBytes(data)], { type: done.mime }));
    void this.storeCiphertext(done.key, ciphertext);
    return done;
  }

  /** Fetches, verifies and decrypts a file. Calls for the same file share one download. */
  download(ref: MediaRef): Promise<Blob> {
    const hit = this.memory.get(ref.key);
    if (hit) return Promise.resolve(hit);
    const running = this.inflight.get(ref.key);
    if (running) return running;
    const job = this.fetchAndDecrypt(ref).finally(() => this.inflight.delete(ref.key));
    this.inflight.set(ref.key, job);
    return job;
  }

  private async fetchAndDecrypt(ref: MediaRef): Promise<Blob> {
    if (mediaSupport(ref) === "update") throw new MediaVersionError(ref.version ?? 0);
    if (isChunked(ref)) {
      const blob = await streamToBlob(this.stream(ref), ref.mime);
      this.memory.set(ref.key, blob);
      return blob;
    }
    let ciphertext = await this.cachedCiphertext(ref.key);
    if (!ciphertext) {
      const { url } = await this.api.mediaUrl(ref.key);
      const res = await this.doFetch(url, { credentials: "omit" });
      if (!res.ok) throw new Error("This file could not be downloaded. Try again.");
      ciphertext = new Uint8Array(await res.arrayBuffer());
    }
    let plain: Uint8Array<ArrayBuffer>;
    try {
      plain = await decryptFile(ciphertext, ref);
    } catch (err) {
      await this.forgetCiphertext(ref.key);
      throw err;
    }
    void this.storeCiphertext(ref.key, ciphertext);
    const blob = new Blob([plain], { type: ref.mime });
    this.memory.set(ref.key, blob);
    return blob;
  }

  /**
   * Plain bytes of a chunked (version 2) file as they arrive, for all of it or for the plain byte
   * range start..end, inclusive. Chunks are fetched with ranged requests and opened one by one.
   */
  stream(ref: MediaRef, opts: StreamOptions = {}): ReadableStream<Uint8Array<ArrayBuffer>> {
    return decryptStream(ref, this.rangeFetcher(ref.key), opts);
  }

  /** Ranged reads of a stored file, asking for a fresh presigned URL when one expires. */
  rangeFetcher(key: string): RangeFetcher {
    return urlRangeFetcher(async () => (await this.api.mediaUrl(key, { chunked: true })).url, this.doFetch);
  }

  private async cachedCiphertext(key: string): Promise<Uint8Array<ArrayBuffer> | null> {
    if (!this.cacheStorage) return null;
    try {
      const cache = await this.cacheStorage.open(CACHE_NAME);
      const res = await cache.match(cacheRequest(key));
      return res ? new Uint8Array(await res.arrayBuffer()) : null;
    } catch {
      return null;
    }
  }

  private async storeCiphertext(key: string, ciphertext: Uint8Array<ArrayBuffer>): Promise<void> {
    if (!this.cacheStorage) return;
    try {
      const cache = await this.cacheStorage.open(CACHE_NAME);
      await cache.put(
        cacheRequest(key),
        new Response(ciphertext, { headers: { "Content-Type": "application/octet-stream" } }),
      );
    } catch {
      // Storage can be full or blocked. The file still opens, it is just fetched again next time.
    }
  }

  private async forgetCiphertext(key: string): Promise<void> {
    if (!this.cacheStorage) return;
    try {
      const cache = await this.cacheStorage.open(CACHE_NAME);
      await cache.delete(cacheRequest(key));
    } catch {
      // Nothing to clean up.
    }
  }
}
